import React from "react";
import { Stack, TextField, Typography } from "@mui/material";
import { ANUploader } from "@answersnow/pkg-theme";

export interface AdditionalAttachment {
  file: File;
  errors: boolean;
}

interface AdditionalInformationSectionProps {
  additionalInformation: string;
  onAdditionalInformationChange: (value: string) => void;
  files: AdditionalAttachment[];
  onFilesChange: (files: AdditionalAttachment[]) => void;
}

const ACCEPTED_IMAGE_TYPES = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
};
const MAX_ATTACHMENT_BYTES = 7 * 1024 * 1024;

/**
 * Additional Information free-text field plus an image uploader, shared across the
 * Generate Document flows. Controlled: the parent flow owns the text and files so it
 * can fold them into its own generate payload (base64 images -> `additionalImages`).
 */
const AdditionalInformationSection: React.FC<AdditionalInformationSectionProps> = ({
  additionalInformation,
  onAdditionalInformationChange,
  files,
  onFilesChange,
}) => {
  const [typeError, setTypeError] = React.useState(false);

  const handleFileUpload = ({
    acceptedFiles,
    fileRejections,
  }: {
    acceptedFiles: File[];
    fileRejections: { file: File; errors: { code: string }[] }[];
  }) => {
    const hasInvalidType = fileRejections.some((rejection) =>
      rejection.errors.some((error) => error.code === "file-invalid-type")
    );
    setTypeError(hasInvalidType);

    const oversized = fileRejections.filter((rejection) =>
      rejection.errors.some((error) => error.code === "file-too-large")
    );
    const next: AdditionalAttachment[] = [
      ...acceptedFiles.map((file) => ({ file, errors: false })),
      ...oversized.map((rejection) => ({ file: rejection.file, errors: true })),
    ];
    onFilesChange([...files, ...next]);
  };

  const handleFileDelete = (target: AdditionalAttachment) => {
    onFilesChange(files.filter((file) => file !== target));
  };

  return (
    <>
      <TextField
        label="Additional Information"
        inputProps={{ "aria-label": "Additional Information" }}
        fullWidth
        multiline
        minRows={3}
        value={additionalInformation}
        onChange={(event) => onAdditionalInformationChange(event.target.value)}
      />
      <Stack sx={{ gap: 3 }}>
        <Typography variant="inputLabel" color="text.secondary">
          Attach additional charting or clinical data to be embedded into the generated document.
        </Typography>
        <ANUploader
          dropzoneFiles={files}
          alreadyUploadedFiles={[]}
          handleFileUpload={handleFileUpload}
          handleFileDelete={handleFileDelete}
          handleLoadedFileDelete={() => {}}
          disableImagePreview
          error={typeError}
          errorText="Please upload a PNG or JPG file"
          maxFileSize={MAX_ATTACHMENT_BYTES}
          useDropzoneOptions={{
            multiple: true,
            accept: ACCEPTED_IMAGE_TYPES,
            maxSize: MAX_ATTACHMENT_BYTES,
          }}
          dropZoneStyle={{ margin: 0, padding: "24px 16px" }}
        />
      </Stack>
    </>
  );
};

export default AdditionalInformationSection;

/**
 * Converts the (non-errored) attachments to base64 data URLs for the MIRA platform
 * context, matching the format of the Chart.js `chartImages` already sent on generate.
 */
export async function buildAdditionalInformationImages(
  files: AdditionalAttachment[]
): Promise<string[]> {
  const validFiles = files.filter((attachment) => !attachment.errors);
  return Promise.all(
    validFiles.map(
      (attachment) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(attachment.file);
        })
    )
  );
}
